// src/quiz/gre-mock6/quantitativeData.js
// GRE Mock 6 — Quantitative Reasoning (hard set, same level as Mock 5).
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL, QC_UNKNOWN] = QC_OPTIONS;

const COMMUTE_TABLE = {
  caption: "400 Survey Respondents by Age Group and Preferred Way of Commuting",
  headers: ["Age group", "Car", "Public transit", "Bicycle", "Total"],
  rows: [
    ["18–29", "30", "50", "20", "100"],
    ["30–44", "70", "40", "10", "120"],
    ["45–59", "80", "25", "5", "110"],
    ["60 and over", "50", "18", "2", "70"],
    ["Total", "230", "133", "37", "400"],
  ],
};

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    given: "x and y are integers and x > y > 0.",
    quantityA: "x/y",
    quantityB: "(x + 1)/(y + 1)",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "Cross-multiply (both denominators are positive): compare x(y + 1) = xy + x with y(x + 1) = xy + y. Since x > y, Quantity A is always greater. Example: 3/2 = 1.5 > 4/3.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    quantityA: "0.1 percent of 0.1 percent of 1,000,000",
    quantityB: "1",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "0.1 percent = 0.001. 0.001 × 0.001 × 1,000,000 = 10⁻³ × 10⁻³ × 10⁶ = 1. Reading \"0.1 percent\" as 0.1 gives the trap answer A.",
  },
  {
    id: 3,
    question: QC_QUESTION,
    given: "n is a positive integer.",
    quantityA: "The number of different prime factors of 2n",
    quantityB: "The number of different prime factors of n",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "If n is even, 2 is already a prime factor of n, so the counts are equal (n = 6: both have 2 and 3). If n is odd, 2n gains the new prime factor 2 (n = 3: 2n = 6 has two, n has one). The relationship depends on n.",
  },
  {
    id: 4,
    question: QC_QUESTION,
    given: "Set S consists of 10 consecutive integers.",
    quantityA: "The median of S",
    quantityB: "The average (arithmetic mean) of S",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "Consecutive integers are evenly spaced, so the list is symmetric about its middle and the mean equals the median. For 1–10, both are 5.5.",
  },
  {
    id: 5,
    question: "If 9ˣ − 9^(x − 1) = 648, what is the value of x?",
    options: ["2", "2.5", "3", "3.5", "4"],
    answer: "3",
    explanation: "Factor out the smaller power: 9^(x − 1)(9 − 1) = 648, so 9^(x − 1) = 81 = 9². Then x − 1 = 2 and x = 3.",
  },
  {
    id: 6,
    question: "A store marks up the price of a shirt 40 percent above its cost and then sells it at a 15 percent discount off the marked price. The store's profit is what percent of the cost?",
    options: ["15%", "19%", "21%", "25%", "34%"],
    answer: "19%",
    explanation: "Selling price = 1.40 × 0.85 = 1.19 times the cost, a 19% profit. Subtracting the percents (40 − 15 = 25) is the trap.",
  },
  {
    id: 7,
    type: "numeric",
    question: "How many different arrangements of the letters in the word LEVEL are possible?",
    answer: 30,
    explanation: "5 letters with L repeated twice and E repeated twice: 5!/(2! × 2!) = 120/4 = 30.",
  },
  {
    id: 8,
    type: "multi",
    question: "If n = 2³ × 3² × 7, which of the following are factors of n?",
    options: ["24", "27", "42", "56", "63", "84", "98"],
    answer: ["24", "42", "56", "63", "84"],
    explanation: "Factor each: 24 = 2³ × 3 ✓, 27 = 3³ ✗ (n has only 3²), 42 = 2 × 3 × 7 ✓, 56 = 2³ × 7 ✓, 63 = 3² × 7 ✓, 84 = 2² × 3 × 7 ✓, 98 = 2 × 7² ✗ (n has only one 7).",
  },
  {
    id: 9,
    question: "A rectangle is inscribed in a circle of radius 5. If one side of the rectangle has length 6, what is the area of the rectangle?",
    options: ["24", "30", "40", "48", "50"],
    answer: "48",
    explanation: "The rectangle's diagonal is a diameter: 10. The other side is √(10² − 6²) = 8, so the area is 6 × 8 = 48.",
  },
  {
    id: 10,
    table: COMMUTE_TABLE,
    question: "Of the respondents who prefer public transit, approximately what percent are under 45 years old?",
    options: ["23%", "45%", "58%", "68%", "75%"],
    answer: "68%",
    explanation: "Transit users under 45: 50 + 40 = 90, out of 133 transit users. 90/133 ≈ 67.7% ≈ 68%. Dividing by all 400 respondents gives the trap 23%.",
  },
  {
    id: 11,
    type: "numeric",
    table: COMMUTE_TABLE,
    question: "If one respondent is chosen at random from those aged 45 or over, what is the probability that the respondent prefers to commute by car? Give your answer as a fraction.",
    answer: 13 / 18,
    explanation: "Respondents aged 45 or over: 110 + 70 = 180. Of these, 80 + 50 = 130 prefer a car. 130/180 = 13/18.",
  },
  {
    id: 12,
    type: "multi",
    table: COMMUTE_TABLE,
    question: "In which age groups did more than 25 percent of the respondents prefer public transit?",
    options: ["18–29", "30–44", "45–59", "60 and over"],
    answer: ["18–29", "30–44", "60 and over"],
    explanation: "18–29: 50/100 = 50%; 30–44: 40/120 ≈ 33%; 45–59: 25/110 ≈ 23%; 60 and over: 18/70 ≈ 25.7%. The oldest group has the fewest transit users but still clears 25%.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: QC_QUESTION,
    given: "a < b < 0",
    quantityA: "a²b",
    quantityB: "ab²",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "A − B = ab(a − b). Both negative, so ab > 0; a < b, so a − b < 0. The difference is negative and B is greater. Check: a = −2, b = −1 gives A = −4, B = −2.",
  },
  {
    id: 14,
    question: QC_QUESTION,
    quantityA: "2³⁰ + 2³⁰",
    quantityB: "4¹⁶",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "2³⁰ + 2³⁰ = 2 × 2³⁰ = 2³¹, while 4¹⁶ = (2²)¹⁶ = 2³². Adding equal powers doubles them; it does not double the exponent.",
  },
  {
    id: 15,
    question: QC_QUESTION,
    given: "Circle C has area 36π, and square S has perimeter 48.",
    quantityA: "The area of a square inscribed in circle C",
    quantityB: "Half the area of square S",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "C has radius 6, so an inscribed square has diagonal 12 and area 12²/2 = 72. S has side 12 and area 144; half of that is 72.",
  },
  {
    id: 16,
    question: QC_QUESTION,
    given: "A number x is chosen at random from the set {−2, −1, 0, 1, 2}.",
    quantityA: "The probability that x² > x",
    quantityB: "1/2",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "x² > x holds for −2 (4 > −2), −1 (1 > −1) and 2 (4 > 2), but not for 0 or 1, where x² = x. The probability is 3/5 > 1/2.",
  },
  {
    id: 17,
    question: QC_QUESTION,
    given: "The average (arithmetic mean) of a, b and c is 20, and the average of a and b is 14.",
    quantityA: "c",
    quantityB: "30",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "a + b + c = 60 and a + b = 28, so c = 32 > 30.",
  },
  {
    id: 18,
    question: "If x² − y² = 48 and x + y = 12, what is the value of x?",
    options: ["4", "6", "8", "10", "12"],
    answer: "8",
    explanation: "x² − y² = (x + y)(x − y), so 12(x − y) = 48 and x − y = 4. Adding x + y = 12 gives 2x = 16, x = 8.",
  },
  {
    id: 19,
    question: "A fair coin is tossed 5 times. What is the probability of getting exactly 3 heads?",
    options: ["1/4", "5/16", "3/8", "1/2", "5/8"],
    answer: "5/16",
    explanation: "There are 2⁵ = 32 equally likely outcomes, and C(5, 3) = 10 of them have exactly 3 heads. 10/32 = 5/16.",
  },
  {
    id: 20,
    type: "numeric",
    question: "A car travels the first half of a trip's distance at 40 miles per hour and the second half at 60 miles per hour. What is its average speed for the whole trip, in miles per hour?",
    answer: 48,
    explanation: "Take a 240-mile trip: 120/40 = 3 hours plus 120/60 = 2 hours, so 240 miles in 5 hours = 48 mph. The car spends more time at the slower speed, so the average is below the trap answer 50.",
  },
  {
    id: 21,
    question: "How many integers from 1 to 200, inclusive, are perfect squares or perfect cubes?",
    options: ["15", "16", "17", "18", "19"],
    answer: "17",
    explanation: "Squares: 1², …, 14² = 196, so 14. Cubes: 1³, …, 5³ = 125, so 5. Both (sixth powers): 1 and 64, so 2. Total 14 + 5 − 2 = 17.",
  },
  {
    id: 22,
    question: "If f(x) = (x + 1)/(x − 1), what is the value of f(f(3))?",
    options: ["1/2", "1", "2", "3", "4"],
    answer: "3",
    explanation: "f(3) = 4/2 = 2, and f(2) = 3/1 = 3.",
  },
  {
    id: 23,
    type: "multi",
    question: "If |a| = 3 and |b| = 5, which of the following could be the value of |a − b|?",
    options: ["0", "2", "3", "5", "8"],
    answer: ["2", "8"],
    explanation: "a = ±3 and b = ±5. a − b can be 3 − 5 = −2, 3 + 5 = 8, −3 − 5 = −8 or −3 + 5 = 2, so |a − b| is 2 or 8.",
  },
  {
    id: 24,
    type: "numeric",
    question: "The sum of the interior angles of a regular polygon is 1,440°. How many sides does the polygon have?",
    answer: 10,
    explanation: "The interior angles of an n-sided polygon sum to (n − 2) × 180°. (n − 2) × 180 = 1,440 gives n − 2 = 8, n = 10.",
  },
  {
    id: 25,
    question: "A right circular cylinder has radius 3 and height 8. A sphere has the same volume as the cylinder. What is the radius of the sphere?",
    options: ["3", "2∛6", "3∛2", "3√2", "6"],
    answer: "3∛2",
    explanation: "Cylinder volume = π × 3² × 8 = 72π. (4/3)πR³ = 72π gives R³ = 54 = 27 × 2, so R = 3∛2.",
  },
  {
    id: 26,
    question: "$5,000 is invested at 10 percent annual interest, compounded annually. How much interest is earned in 3 years?",
    options: ["$1,500", "$1,550", "$1,655", "$1,665", "$1,750"],
    answer: "$1,655",
    explanation: "5,000 × 1.1³ = 5,000 × 1.331 = 6,655, so the interest is $1,655. Simple interest would give the trap $1,500.",
  },
  {
    id: 27,
    type: "numeric",
    question: "What is the remainder when 3¹⁰⁰ is divided by 7?",
    answer: 4,
    explanation: "Powers of 3 leave remainders 3, 2, 6, 4, 5, 1 when divided by 7, then repeat every 6. 100 = 6 × 16 + 4, so 3¹⁰⁰ has the same remainder as 3⁴ = 81 = 11 × 7 + 4: remainder 4.",
  },
];
